import React, { useEffect, useMemo, useRef, useState } from "react";
import useShellScrollLock from "../../hooks/useShellScrollLock";
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";
import routes from "../../routes";
import LegacyRedirect from "../Routing/LegacyRedirect";
import NotFound from "../../Views/NotFound/NotFound";
import { AdminPageSkeleton } from "../Loading/AdminDataSkeleton";
import Sidebar from "../Sidebar";
import { MainNav } from "../MuiTable";
import { useMediaQuery } from "@mui/material";
import AxiosInstance from "../../utils/axiosInstance";
import { Grid } from "@mui/material";
import { ThemeProvider, createTheme } from "@mui/material/styles";
// JOB-7066: merchant branding (palette for the viewer's appearance, fonts).
import { BrandContext, useCompanyBrand } from "../../contexts/BrandContext";
import { withBrandTheme } from "../../utils/paintBrand";
import {
  AdminDarkModeProvider,
  useAdminDarkMode,
} from "../../contexts/AdminDarkModeContext";
import { updateCompanyNameFromToken, setPageTitle, getPageTitleFromRoute } from "../../utils/pageTitle";
import {
  CJ_PORTAL_CONTENT_OFFSET_COLLAPSED_PX,
  CJ_PORTAL_CONTENT_OFFSET_EXPANDED_PX,
} from "../../utils/cjPortalSidebarMetrics";
import { getItem } from "../../utils/storage";
import { useUserType } from "../../contexts/UserTypeContext";
import { useProduct } from "../../contexts/ProductContext";
import useLimoTheme from "../../hooks/useLimoTheme";
import { isStaffPathHiddenByMode } from "../Sidebar/cjNavModeDefaults.js";
import {
  getStaffNavLabel,
  isStaffPathHidden,
} from "../Permissions/donorPermissionConfig";
import "./admin-dark-overrides.css";
import { applyColorScheme } from "../../utils/colorScheme";

/* JOB-6340 — route guarding by MODULE, not by nav visibility.
   The nav filter below already hides a module a worker has no grant for, but
   hiding a row never stopped anyone typing the URL. getRoutes already carries
   two redirects for exactly that (JOB-4334 for /worker-reports*, JOB-5324 for
   donor routes); this is the third, driven by the permission matrix.

   Keyed on route.module rather than route.path on purpose. The Customer module
   has SEVEN staff routes and only ONE (/worker-customer) is isDisplay:true:

     /worker-customer                isDisplay true
     /customer-details               isDisplay false
     /customer-details/:id           isDisplay false   <- the reported detail page
     /add-customer                   isDisplay false
     /edit-customer/:id              isDisplay false
     /worker-property-details        isDisplay false
     /worker-property-details/:id    isDisplay false

   A path- or nav-list-based guard would block the list and leave the other six
   reachable, which is a hollow fix. Every route already declares its module, so
   one entry covers the whole family.

   Scoped to Customer deliberately. The same isDisplay:false gap exists for
   Quotes / Contract / Invoice / TimeTracking, but inventing mappings for
   nineteen modules unverified risks locking legitimate workers out of a module,
   which is worse than the exposure. Those need their own ticket and their own
   per-module verification. */
const STAFF_MODULE_PERMISSION = {
  Customer: "Customers",
  /* JOB-6655 follow-up: routes.jsx gave the worker /invoice-payment route
     `module: "InvoicePayment"` so this guard would block an ungranted worker
     who typed the URL -- but the module was never registered here, so
     STAFF_MODULE_PERMISSION[prop.module] was undefined and `isModuleDenied`
     could not become true. The guard was inert. */
  InvoicePayment: "InvoicePayment",
  /* JOB-6646 retest: routes.jsx already gave /worker-pos `module: "POS"`, but
     the module was never registered here -- the identical omission the note
     above records for InvoicePayment, so the URL-typing guard was inert for
     POS as well. Hiding the row and blocking the route are meant to be the
     same decision; this is the half that makes the second one real. */
  POS: "POS",
  // JOB-7274: /worker-transactions, same grant as the POS screen.
  Transactions: "POS",
};

function StaffMemberInner() {
  const { darkMode } = useAdminDarkMode();
  const brand = useCompanyBrand(darkMode);
  const mainContent = useRef(null);
  const scrollShell = useRef(null);
  // JOB-7517: the shell is the only scroller; the document must not scroll (see useShellScrollLock).
  useShellScrollLock();
  const location = useLocation();
  const navigate = useNavigate();
  const [isSidebarClosed, setIsSidebarClosed] = useState(false);
  const [isSidebarDisplay, setIsSidebarDisplay] = useState(true);
  const isMediumScreen = useMediaQuery("(max-width:767px)", { noSsr: true });
  const { CompanyUrl } = useParams();
  const baseUrl = import.meta.env.VITE_BASE_API;
  const [staffData, setStaffData] = useState(undefined);
  const [routesData, setRoutesData] = useState([]);
  /* JOB-6340: permission keys the worker has NO grant for. Empty until the
     permission fetch resolves, which keeps the guard FAIL-OPEN: an API hiccup
     renders exactly what it renders today rather than locking a worker out of
     their own portal. The server-side gates (workerModulePermission.js) are the
     enforcement boundary; this is defence in depth for the UI. */
  const [deniedPermissions, setDeniedPermissions] = useState(() => new Set());
  // JOB-5324: the staff portal must follow the tenant's product. On a donor
  // tenant the job-product rows are dropped and the shared entities take the
  // charity vocabulary, matching what the admin sidebar already does.
  const { userType } = useUserType();
  const isDonationMode = userType === "donor";
  // CLM-005 §9: the staff portal had donorOnly but no limoOnly, so a limo
  // worker page would have appeared to every contractor worker too. Same
  // source the admin sidebar uses for the same flag.
  const { isLimo: isLimoMode } = useProduct();
  // The limo palette, which this layout never attached: chauffeurs saw the
  // contractor navy rail and an amber avatar. See hooks/useLimoTheme.
  const limoShell = useLimoTheme(isLimoMode);

  // Reset sidebar state when screen size changes
  useEffect(() => {
    if (!isMediumScreen) {
      setIsSidebarDisplay(true);
      setIsSidebarClosed(false);
    } else {
      setIsSidebarDisplay(false);
    }
  }, [isMediumScreen]);

  useEffect(() => {
    // JOB-5088: the real scroller is the outer 100vh/overflow:auto
    // `.admin-shell` Grid, not `mainContent` (no scroll context).
    document.documentElement.scrollTop = 0;
    document.scrollingElement.scrollTop = 0;
    if (mainContent.current) {
      mainContent.current.scrollTop = 0;
    }
    if (scrollShell.current) {
      scrollShell.current.scrollTop = 0;
    }

    const title = getPageTitleFromRoute(location.pathname);
    setPageTitle(title);
  }, [location]);

  useEffect(() => {
    if (darkMode) {
      document.body.classList.add("admin-dark-active");
    } else {
      document.body.classList.remove("admin-dark-active");
    }
    // LIMO-205: <html> has to say the same thing the page looks like. The
    // browser reads color-scheme for native controls and scrollbars, and CSS
    // reads the attribute; index.html sets both to light before React boots
    // and nothing used to update them afterwards.
    applyColorScheme(darkMode);
    return () => {
      document.body.classList.remove("admin-dark-active");
      applyColorScheme(false);
    };
  }, [darkMode]);

  const workerPortalTheme = useMemo(
    () =>
      createTheme(
        withBrandTheme({
          palette: {
            mode: darkMode ? "dark" : "light",
            primary: {
              main: darkMode ? "#90caf9" : "#1976d2", // brand-exempt: MUI palette value (needs a parseable color); brand applied via withBrandTheme
            },
            secondary: {
              main: darkMode ? "#f48fb1" : "#dc004e",
            },
            background: {
              default: darkMode ? "#0f1419" : "#f7f8fa",
              paper: darkMode ? "#1a2332" : "#ffffff",
            },
            ...(darkMode
              ? {
                  text: {
                    primary: "#e8eaed",
                    secondary: "#9aa0a6",
                  },
                  divider: "#2d3b4e",
                }
              : {}),
          },
          components: {
            MuiPaper: {
              styleOverrides: {
                root: {
                  backgroundImage: "none",
                },
              },
            },
          },
          typography: {
            fontFamily: "'Poppins', sans-serif",
            h1: {
              fontFamily: "'Poppins', sans-serif",
              fontWeight: 600,
            },
            h2: {
              fontFamily: "'Poppins', sans-serif",
              fontWeight: 500,
            },
            body1: {
              fontFamily: "'Poppins', sans-serif",
            },
          },
        }, brand.data, darkMode)
      ),
    [darkMode, brand.data]
  );

  useEffect(() => {
    const fetchStaffData = async () => {
      try {
        const response = await AxiosInstance.get(
          `/v1/worker/get/${getItem("worker_id")}`
        );
        setStaffData(response?.data?.data?.permissions);
      } catch (error) {
        console.error("Error fetching staff data:", error);
      }
    };

    if (location.pathname.includes("/staff-member")) {
      fetchStaffData();
    }
  }, [baseUrl]);

  useEffect(() => {
    if (staffData) {
      const filterRoutes = () => {
        const data = routes.filter(
          (item) =>
            item.layout === `/:CompanyUrl/staff-member` && item.isDisplay
        );

        const offModule = {
          ViewOnly: false,
          ViewCreateAndEdit: false,
          ViewCreateEditAndDelete: false,
        };
        const permissionChecks = {
          Quotes: staffData?.Quotes,
          Contract: staffData?.Contract,
          Invoice: staffData?.Invoice,
          Schedule: staffData?.Schedule,
          Appoinment: staffData?.Appoinment,
          CustomersProperties: staffData?.CustomersProperties,
          Customers: staffData?.Customers,
          Reports: staffData?.Reports,
          TimeTrackingAndTimesheets: staffData?.TimeTrackingAndTimesheets,
          MaterialsLabor: staffData?.MaterialsLabor,
          /* JOB-6655 follow-up: without this key the grant took part in
             nothing here -- not `denied`, not the path allow-list below -- so
             neither guard could read it. */
          InvoicePayment: staffData?.InvoicePayment,
          /* JOB-6646 retest: this key was missing, and that is the whole of
             the reported access-control gap. `/worker-pos` IS mapped in
             pathToPermissionMap below, but the map resolves to
             permissionChecks["POS"] -- and with no key here that is
             `undefined`, so the filter's last line evaluated
             `hasNoPermissions(undefined)` -> undefined -> `!undefined` ->
             true, and the row was shown UNCONDITIONALLY. Not only to the
             revoked worker the retest found: measured against the shipped
             object, granted, revoked and legacy records all came back
             `shown`, so POS was never gated at all.

             Default-off spread for the same reason ServiceRequests and
             Automations have one: a permission document predating the field
             must hide the row, not pass `undefined` through and show it to
             everyone. Adding the key also puts "POS" in the `denied` set
             below, which is what arms the route guard. */
          POS: { TakePayments: false, ...staffData?.POS },
          ServiceRequests: { ...offModule, ...staffData?.ServiceRequests },
          // JOB-5631: flag-shaped grant ({View, Manage, RunManually}) for the
          // Automation Engine. Default-off spread mirrors ServiceRequests so a
          // permission doc predating the field hides the row instead of
          // passing `undefined` through hasNoPermissions (which would show
          // the row to everyone).
          Automations: {
            View: false,
            Manage: false,
            RunManually: false,
            ...staffData?.Automations,
          },
          // JOB-7103: same default-off spread, so a permission document
          // predating Branding hides the row instead of showing it to all.
          Branding: {
            View: false,
            Manage: false,
            ...staffData?.Branding,
          },
        };

        const hasNoPermissions = (permissions) => 
          permissions && Object.values(permissions).every(value => !value);

        const pathToPermissionMap = {
          // JOB-5631: Automation Engine row (both products). Any Automations
          // flag shows the row; the API's requireAutomation gate enforces the
          // exact level per call (Manage implies View server-side).
          "/automations": "Automations",
          // JOB-7103: the Branding hub (WorkerPermission.Branding).
          "/branding-setting": "Branding",
          "/worker-quote": "Quotes",
          "/worker-service-requests": "ServiceRequests",
          "/worker-customer": "Customers",
          "/worker-schedule": "Schedule",
          "/worker-contract": "Contract",
          /* JOB-6534: a worker WITH Add Contract + View Contract was bounced
             to the dashboard when they clicked Add Contract. /add-contract is
             a staff-layout route with isDisplay:true and had no entry here, so
             the filter's last line evaluated `undefined && ...` -- dropped for
             every worker whatever the tenant granted -- and the JOB-6342 guard
             below then redirected the URL it had just dropped. Same shape as
             the JOB-5747 note above.

             Mapped rather than switched to isDisplay:false (which is what the
             admin twin at routes.jsx:1315 is) because POST /v1/contract is
             restrictTo("Worker","Company","Admin") with NO requireModule gate
             -- this redirect is the only thing standing between an ungranted
             worker and a created contract, so removing it from the guard's
             universe would open a real hole, not a cosmetic one. */
          "/add-contract": "Contract",
          "/worker-invoice": "Invoice",
          // JOB-6646: without this entry the row would be shown to EVERY
          // worker -- the filter below keeps any path it has no mapping for.
          "/worker-pos": "POS",
          // JOB-7274: the Transactions page shows the POS screen's list.
          "/worker-transactions": "POS",
          /* JOB-6551: the same two-step as /add-contract above, on the
             invoice detail page. isDisplay:true (routes.jsx:2493) with no
             entry here meant the filter's last line read `undefined && ...`
             -- dropped for every worker whatever the tenant granted -- and
             the JOB-6342 guard then redirected the URL it had just dropped.
             A real lockout, not a cosmetic one: seven call sites navigate
             here and there is no other way to open an invoice, so a worker
             WITH Invoice permission clicking an invoice landed on the
             dashboard. (OverView x2, MuiTable row click, NotificationPage,
             PropertyDetails, Invoice.jsx x2.)

             Any Invoice flag is the right rung here, unlike /add-contract,
             which needed the ADD grant because it is a create form with an
             ungated POST behind it. This is a read-only detail view; a
             worker who may see the Invoices grid may see a row in it.

             Found by sweeping the whole staff table for JOB-6534's shape
             rather than waiting for the next report. That sweep also turned
             up /invoice-payment (routes.jsx:2503), which is the identical
             defect -- AddInvoice.jsx:664 sends a WORKER to
             /staff-member/invoice-payment after a collect invoice, so they
             are bounced to the dashboard holding an invoice they cannot
             take payment on. It is deliberately NOT mapped here: that route
             is the payment-taking screen, so mapping it widens who can take
             a payment, which is a product decision rather than a routing
             fix. Recorded on JOB-6551 for that call. */
          "/worker-invoice-detail": "Invoice",
          "/worker-materials-labor": "MaterialsLabor",
          // JOB-6931: the renamed route, same MaterialsLabor grant.
          "/worker-products-services": "MaterialsLabor",
          "/worker-appointments": "Appoinment",
          "/worker-todays-appointments": "Appoinment",
          "/worker-time-entry": "TimeTrackingAndTimesheets",
          // JOB-6269: this entry cannot fire. The filter above narrows on
          // `item.isDisplay` first, and the /worker-reports route is
          // isDisplay:false (routes.jsx, JOB-3935), so the row is dropped
          // before any permission is read. Kept rather than deleted because it
          // is the seam a future worker-safe reports hub would reuse -- if that
          // route is ever flipped to isDisplay:true this mapping is already
          // correct. Also update getModuleNote() in donorPermissionConfig.js
          // if that happens, or the admin grid will keep saying the grant does
          // nothing.
          "/worker-reports": "Reports",
          // JOB-5324: donor staff surfaces reuse the keys the permission
          // grid relabels for charity tenants, so one grant drives both the
          // nav row here and the API gate in workerModulePermission.js.
          "/donor-crm/pledges": "Quotes",
          "/grants": "Contract",
          "/accept-donation": "Invoice",
          // JOB-5324 (QA round 3): the rows below were baseline — visible to
          // every staff member with zero permissions granted (JOB-5362), which
          // QA correctly failed as over-exposure. There is still no per-module
          // WorkerPermission key for any of them, so the whole donor-CRM /
          // fundraising suite follows the "Donors" toggle (Customers key):
          // an admin who trusts a worker with the donor list trusts them with
          // the donor CRM built on top of it. One toggle, nav and API agree —
          // the matching requireModule("Customers") gates live in
          // donorCrmRoutes / matchingGiftsRoutes / eventRoutes /
          // givingDayRoutes / volunteersRoutes / donorDedupeRoutes.
          "/events": "Customers",
          "/donor-crm/campaigns": "Customers",
          "/donor-crm/pipeline": "Customers",
          "/donor-crm/households": "Customers",
          "/donor-crm/tasks": "Customers",
          "/donor-crm/segments": "Customers",
          "/donor-crm/interests": "Customers",
          "/donor-crm/engagement": "Customers",
          "/donor-crm/beneficiaries": "Customers",
          "/donor-crm/sponsorships": "Customers",
          "/donor-crm/duplicates": "Customers",
          "/donor-crm/tier-sets": "Customers",
          "/matching-gifts": "Customers",
          "/match-challenges": "Customers",
          "/giving-days": "Customers",
          "/volunteers": "Customers",
          "/volunteers/shifts": "Customers",
        };

        // JOB-5324 (QA round 3): Text to Donate's API is
        // restrictTo("Company","Admin") — a Worker token is rejected outright,
        // so no grant can make that page work for staff. Hide the row for all
        // staff rather than map it to a grant that would open a broken page.
        const staffHiddenDonorPaths = new Set(["/donor-crm/text-to-give"]);

        // CloudTime-parity worker entries (Time Clock / Open Shifts /
        // Announcements / Time Off / Face Enrollment) are not gated by the
        // per-tenant worker permission matrix — backend already enforces
        // role-based access (Worker | Company | Admin) and these are
        // baseline T&A features every worker should see. Allow-list them
        // alongside the dashboard so the staff sidebar filter doesn't drop
        // them.
        const cloudTimeAlwaysShow = new Set([
          "/worker-time-clock",
          "/worker-open-shifts",
          // JOB-5747: "My Shifts" was missing here, so it never reached a
          // worker's sidebar on any tenant. It has no pathToPermissionMap entry
          // either, and being in neither list means the filter's last line
          // evaluates `undefined && ...` — the row is dropped for every worker
          // regardless of what the tenant granted, which is why no permission
          // change could surface it.
          //
          // It belongs here rather than behind a grant: it is the
          // initiation-side companion to Open Shifts directly above (JOB-5741
          // added the pair and only the pickup side was allow-listed), the same
          // baseline T&A shape, and its endpoints are already
          // restrictTo("Worker", "Company", "Admin") — GET /v1/schedule scoped
          // to self plus the release / swap transactions. It is also already
          // listed in HIDDEN_STAFF_PATHS_DONOR, which only makes sense if the
          // row is meant to show on a job tenant.
          "/worker-my-shifts",
          "/worker-announcements",
          "/worker-time-off",
          "/worker-face-enrollment",
        ]);

        const newRoutes = data.filter(item => {
          // JOB-5324: job-product rows never appear on a donor tenant. This
          // must run FIRST — the cloudTimeAlwaysShow allow-list below would
          // otherwise force Time Clock / Open Shifts / Time Off / Face
          // Enrollment / Announcements through regardless of mode.
          if (isStaffPathHidden(item?.path, isDonationMode)) return false;
          // JOB-5324: donor-product rows exist only on a donor tenant.
          if (item?.donorOnly && !isDonationMode) return false;
          // Limo rows (the chauffeur's own trips) exist only on a limo tenant.
          if (item?.limoOnly && !isLimoMode) return false;
          // ...and a limo chauffeur does not get the contractor Jobs / Visits /
          // Inventory rows, whatever the permission grid grants (CLM-002 §2.4).
          if (isStaffPathHiddenByMode(item?.path, isLimoMode ? "limo" : "")) return false;
          // ...and on a limo tenant they are shown. They are not behind the
          // worker permission matrix: the /v1/limo/driver API decides, by
          // whether this login is an active chauffeur, and the page explains a
          // refusal. Without this line the row reached the end of the filter
          // in neither list below and was dropped, and allowedPaths then
          // redirected the URL to the dashboard - JOB-5747's My Shifts gap.
          if (item?.limoOnly) return true;
          // JOB-5324 (QA round 3): rows whose API a Worker token can never
          // call, whatever is granted.
          if (staffHiddenDonorPaths.has(item?.path)) return false;
          // JOB-5324 (QA round 3): every donor row now carries a permission
          // mapping, so this baseline branch matches nothing today. It stays
          // as the designed default for future donor rows: a newly mounted
          // path is visible until it is either mapped to a grant above or
          // hidden — the same treatment cloudTimeAlwaysShow gives the CJM
          // Time & Attendance rows.
          if (
            item?.donorOnly &&
            isDonationMode &&
            !pathToPermissionMap[item?.path]
          ) {
            return true;
          }

          if (item.path === "/index") return true;
          if (cloudTimeAlwaysShow.has(item?.path)) return true;

          const permission = pathToPermissionMap[item?.path];
          
          // If Customers permission is completely off, hide the customer panel
          if (item?.path === "/worker-customer") {
            const customersPermission = permissionChecks["Customers"];
            
            if (hasNoPermissions(customersPermission)) {
              return false;
            }
            
            // Show the panel if Customers permission has at least one permission enabled
            return true;
          }
          
          // Time Entry - show if any TimeTrackingAndTimesheets permission is enabled
          if (item?.path === "/worker-time-entry") {
            const timeTrackingPermission = permissionChecks["TimeTrackingAndTimesheets"];
            
            if (hasNoPermissions(timeTrackingPermission)) {
              return false;
            }
            
            // Show the panel if TimeTrackingAndTimesheets permission has at least one permission enabled
            return true;
          }
          
          /* JOB-6534: Add Contract needs the ADD grant, not merely any
             Contract flag. The generic line below shows a row when ANY flag is
             set, which is right for a list but wrong for a create form -- and
             it would matter here, because POST /v1/contract carries no
             requireModule gate, so a View-only worker who reached this form
             could actually save a contract. Today they are redirected; this
             keeps them redirected. Same per-path shape as the two cases above.

             "edit" on the ladder is ViewCreateAndEdit OR
             ViewCreateEditAndDelete (workerModulePermission.js:39-41). */
          if (item?.path === "/add-contract") {
            const contractPermission = permissionChecks["Contract"];
            return (
              contractPermission?.ViewCreateAndEdit === true ||
              contractPermission?.ViewCreateEditAndDelete === true
            );
          }

          return permission && !hasNoPermissions(permissionChecks[permission]);
        });

        // JOB-6342: `newRoutes` above only ever fed the SIDEBAR list
        // (`routesData`) -- the actual `<Route>` elements below are built
        // from the raw, unfiltered `routes` import (`getRoutes(routes)`), so
        // a worker whose Customers permission is fully off had the nav row
        // hidden but the route itself stayed fully reachable by typing the
        // URL directly. Redirect away from any staff-layout path that
        // `newRoutes` just dropped for permission reasons, so hiding a row
        // and blocking it are the same decision instead of two.
        const allowedPaths = new Set(newRoutes.map((item) => item?.path));

        /* JOB-6655 follow-up -- the reason the button worked and the screen
           did not.
           `newRoutes` is the SIDEBAR list, and its last line is
           `return permission && !hasNoPermissions(...)`, so a staff path with
           no pathToPermissionMap entry is falsy and gets dropped. Dropped
           means not in allowedPaths, which means the redirect below fires. Any
           worker clicking Collect Payment therefore landed on
           /staff-member/index -- the dead end recorded on JOB-6551 and left
           unmapped there pending "a product decision rather than a routing
           fix" about who may take a payment.
           JOB-6655 IS that decision: InvoicePayment.CollectPayment, paired
           with an Invoice edit grant. So the path is allowed now -- but keyed
           on the grant, not simply mapped, because mapping it in
           pathToPermissionMap would also put an "Invoice Payment" row in the
           worker's sidebar. This screen is contextual: it is opened from an
           invoice and reads invoiceData off location.state, so a nav row would
           open an empty payment form. Gating and nav visibility are otherwise
           the same decision by design; this is the one route that needs them
           apart. */
        /* The condition is the SAME pair the three entry points gate on
           (Invoice.jsx, InvoiceDetail.jsx, AddInvoice.jsx): the payment grant
           AND an Invoice edit grant. Checking only the payment grant would let
           a view-only worker who holds it reach the screen by typing the URL
           while all three buttons stay hidden from them -- a guard disagreeing
           with the UI it is supposed to back. */
        const invoiceGrant = permissionChecks.Invoice;
        const canCollectPayment =
          !hasNoPermissions(permissionChecks.InvoicePayment) &&
          Boolean(
            invoiceGrant?.ViewCreateAndEdit ||
              invoiceGrant?.ViewCreateEditAndDelete
          );
        if (canCollectPayment) {
          allowedPaths.add("/invoice-payment");
        }

        const currentStaffPath = data.find(
          (item) => location.pathname === `/${CompanyUrl}/staff-member${item.path}`
        )?.path;
        if (currentStaffPath && !allowedPaths.has(currentStaffPath)) {
          navigate(`/${CompanyUrl}/staff-member/index`, { replace: true });
          return;
        }

        // JOB-5324: clone before relabelling — `routes` is a module-level
        // singleton shared with the admin sidebar, so assigning item.name
        // would leak the charity label across portals for the whole session.
        /* JOB-6534: /add-contract has to stay in `newRoutes` above so the
           permission guard sees it, but it is a form reached from the
           Contracts grid, not a destination -- the admin sidebar does not
           list it either. Dropped from the nav list only, so gating and
           navigation stay separate decisions. */
        /* JOB-6551: /worker-invoice-detail joins it for the same reason and
           with the same consequence if omitted. It needs an invoice id from
           router state, so a sidebar row pointing at it would open an empty
           page -- and because the route was dropped entirely until now, a
           new row is exactly what mapping it would otherwise produce. */
        const staffFormPathsHiddenFromNav = new Set([
          "/add-contract",
          "/worker-invoice-detail",
        ]);
        const labelledRoutes = newRoutes
          .filter((item) => !staffFormPathsHiddenFromNav.has(item?.path))
          .map((item) => {
            const name = getStaffNavLabel(item?.path, item?.name, isDonationMode);
            return name === item?.name ? item : { ...item, name };
          });

        setRoutesData(labelledRoutes);

        // JOB-6340: same permissionChecks / hasNoPermissions the nav filter
        // uses, so the route guard and the sidebar can never disagree.
        const denied = new Set(
          Object.keys(permissionChecks).filter((k) =>
            hasNoPermissions(permissionChecks[k])
          )
        );
        setDeniedPermissions(denied);
      };
      filterRoutes();
    }
    // JOB-6342: re-run the redirect check on every navigation, not just when
    // staffData first loads -- a worker already on an allowed page can still
    // type a forbidden staff URL into the address bar without staffData
    // changing at all.
  }, [staffData, isDonationMode, isLimoMode, location.pathname, CompanyUrl, navigate]);

  const getRoutes = (routes) => {
    const workerDashboard = `/${CompanyUrl}/staff-member/index`;
    return routes?.map((prop, key) => {
      if (prop?.layout === "/:CompanyUrl/staff-member" && !prop?.isCollapse) {
        // JOB-4334: the worker Reports hub and its financial sub-reports
        // (transaction / refund / void / recurring / payment-success) expose
        // company financials and are intentionally hidden from the Worker
        // sidebar (JOB-3935). They were still mounted here, so a Worker could
        // reach them by typing the URL. Redirect any /worker-reports* path to
        // the worker dashboard instead of rendering the report component.
        const isFinancialReportRoute =
          prop?.path === "/worker-reports" ||
          String(prop?.path || "").startsWith("/worker-reports/");
        // JOB-5324: the donor staff routes are mounted for every staff
        // portal, so a contractor worker could reach the donor CRM by typing
        // the URL even though the nav row is hidden. Redirect instead.
        const isDonorRouteOffMode = Boolean(prop?.donorOnly) && !isDonationMode;
        // The same for a limo route typed into a contractor or donor tenant.
        const isLimoRouteOffMode = Boolean(prop?.limoOnly) && !isLimoMode;
        // JOB-6340: the worker has no grant at all for this route's module.
        const modulePermission = STAFF_MODULE_PERMISSION[prop?.module];
        const isModuleDenied =
          Boolean(modulePermission) && deniedPermissions.has(modulePermission);
        return (
          <Route
            path={prop?.path}
            element={
              isFinancialReportRoute || isDonorRouteOffMode || isLimoRouteOffMode || isModuleDenied ? (
                <Navigate to={workerDashboard} replace />
              ) : (
                prop?.component
              )
            }
            key={key}
            exact
          />
        );
      } else if (prop?.children) {
        return prop?.children?.map((item) => (
          <Route
            path={item?.path}
            element={item?.component}
            key={key}
            exact
          />
        ));
      }
      return null;
    });
  };

  return (
    <BrandContext.Provider value={brand}>
    <ThemeProvider theme={workerPortalTheme}>
    <Grid
      ref={scrollShell}
      className={`admin-shell${darkMode ? " admin-shell--dark" : ""}${limoShell}`}
      style={{
        backgroundColor: "var(--app-page-background)",
        overflow: "auto",
        overflowX: "hidden",
      }}
    >
      <Sidebar
        layout={`/:CompanyUrl/staff-member`}
        isSidebarClosed={isSidebarClosed}
        setIsSidebarClosed={setIsSidebarClosed}
        isSidebarDisplay={isSidebarDisplay}
        setIsSidebarDisplay={setIsSidebarDisplay}
        routesData={routesData}
      />

      <MainNav
        layout="/:CompanyUrl/staff-member"
        isSidebarDisplay={isSidebarDisplay}
        setIsSidebarDisplay={setIsSidebarDisplay}
        isSidebarClosed={isSidebarClosed}
      />
      <Grid
        className="main-content"
        ref={mainContent}
        sx={{
          boxSizing: "border-box",
          overflowX: "hidden",
          // JOB-5510: same flush-against-the-sidebar issue the superadmin layout
          // had — offset by the sidebar width plus clearance, from the shared
          // constants, so this portal matches the company one too.
          marginLeft: !isMediumScreen
            ? isSidebarClosed
              ? `${CJ_PORTAL_CONTENT_OFFSET_COLLAPSED_PX}px`
              : `${CJ_PORTAL_CONTENT_OFFSET_EXPANDED_PX}px`
            : 0,
          width: !isMediumScreen
            ? `calc(100% - ${
                isSidebarClosed
                  ? CJ_PORTAL_CONTENT_OFFSET_COLLAPSED_PX
                  : CJ_PORTAL_CONTENT_OFFSET_EXPANDED_PX
              }px)`
            : "100%",
          maxWidth: !isMediumScreen
            ? `calc(100% - ${
                isSidebarClosed
                  ? CJ_PORTAL_CONTENT_OFFSET_COLLAPSED_PX
                  : CJ_PORTAL_CONTENT_OFFSET_EXPANDED_PX
              }px)`
            : "100%",
          minWidth: 0,
          // Match Admin layout gutters: use a centered inner wrapper on desktop,
          // keep lightweight padding on small screens.
          px: { xs: "15px", sm: "15px", md: 0 },
          pb: 2,
        }}
      >
        <Grid
          sx={{
            width: { xs: "100%", md: "98%" },
            maxWidth: "100%",
            boxSizing: "border-box",
            minWidth: 0,
            mx: { xs: 0, md: "auto" },
          }}
        >
          <React.Suspense fallback={<AdminPageSkeleton minHeight="60vh" />}>
            <Routes>
              {getRoutes(routes)}
              {/* JOB-6931: the worker page moved to /worker-products-services. */}
              <Route
                path="/worker-materials-labor"
                element={<LegacyRedirect to={`/${CompanyUrl}/staff-member/worker-products-services`} />}
              />
              {/* JOB-3220 + JOB-3225: real 404 instead of silent /index redirect */}
              <Route path="*" element={<NotFound homePath={`/${CompanyUrl}/staff-member/index`} />} />
            </Routes>
          </React.Suspense>
        </Grid>
      </Grid>
    </Grid>
    </ThemeProvider>
    </BrandContext.Provider>
  );
}

const StaffMember = () => (
  <AdminDarkModeProvider>
    <StaffMemberInner />
  </AdminDarkModeProvider>
);

export default StaffMember;
